import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Popper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import { formatVideoTimecode, parseVideoTimeInput } from '../utils/videoTime';

import { useVideoPlaybackRegistry } from './VideoPlaybackRegistryProvider';

import type { VideoElement } from '@/shared/types/slide';

// ---------------------------------------------------------------------------
// VideoSettingsControl
//
// Floating, element-anchored settings for a selected video. A small gear
// button sits at the element's corner (positioned by the caller via `style`);
// clicking it opens a Popper panel beside the element with the per-element
// knobs: start / end (timecode in the UI, stored as integer seconds), autoplay,
// mute, force-captions, alt.
//
// Notes:
//   - Start/end are stored as seconds because that's what the YouTube embed
//     URL takes and what Google Slides' updateVideoProperties accepts (after
//     the serializer multiplies by 1000).
//   - Enabling Autoplay also enables Mute, because browsers/YouTube block
//     un-muted autoplay — we just do the right thing rather than explain it.
//   - The Popper is non-modal (no focus trap / scroll-lock) so it doesn't
//     fight the canvas's own scroll and Ctrl/Cmd-wheel zoom. Outside-click and
//     Escape close it; the close listener excludes the gear button so the
//     toggle doesn't immediately re-close.
// ---------------------------------------------------------------------------

interface VideoSettingsControlProps {
  element: VideoElement;
  onUpdateContent: (patch: Partial<VideoElement['content']>) => void;
  /** Absolute position for the gear button within the canvas container. */
  style?: React.CSSProperties;
}

const TIME_FORMAT_ERROR = 'Use seconds, m:ss, or h:mm:ss.';

type TimeField = 'start' | 'end';

type TimeCommitResult =
  | {
      ok: true;
      patch: Partial<VideoElement['content']>;
      startInput: string;
      endInput: string;
    }
  | {
      ok: false;
      startError: string | null;
      endError: string | null;
    };

function parseTimeInputs(startInput: string, endInput: string): TimeCommitResult {
  const parsedStart = parseVideoTimeInput(startInput);
  const parsedEnd = parseVideoTimeInput(endInput);

  if (parsedStart === false || parsedEnd === false) {
    return {
      ok: false,
      startError: parsedStart === false ? TIME_FORMAT_ERROR : null,
      endError: parsedEnd === false ? TIME_FORMAT_ERROR : null,
    };
  }

  const startSeconds = parsedStart === null || parsedStart <= 0 ? undefined : parsedStart;
  const endSeconds = parsedEnd === null ? undefined : parsedEnd;
  const effectiveStartSeconds = startSeconds ?? 0;
  let endError: string | null = null;

  if (typeof endSeconds === 'number' && endSeconds <= effectiveStartSeconds) {
    endError = 'End must be after Start.';
  }

  if (endError) {
    return { ok: false, startError: null, endError };
  }

  return {
    ok: true,
    patch: { startSeconds, endSeconds },
    startInput: formatVideoTimecode(startSeconds),
    endInput: formatVideoTimecode(endSeconds),
  };
}

const VideoSettingsControl: React.FC<VideoSettingsControlProps> = ({
  element,
  onUpdateContent,
  style,
}) => {
  const { content } = element;
  const { getCurrentTime, hasPlayer } = useVideoPlaybackRegistry();
  const hasLivePreview = hasPlayer(element.id);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const previousElementIdRef = useRef(element.id);
  // popper.js Instance is structurally assignable to this; we only need update().
  const popperInstanceRef = useRef<{ update: () => void } | null>(null);

  // The gear's anchor moves via style updates (canvas scroll / zoom), which
  // popper.js doesn't observe on its own — nudge it to recompute when the
  // caller-supplied position changes while the panel is open.
  useEffect(() => {
    if (open) popperInstanceRef.current?.update();
  }, [open, style?.top, style?.left]);

  const [startInput, setStartInput] = useState(formatVideoTimecode(content.startSeconds));
  const [endInput, setEndInput] = useState(formatVideoTimecode(content.endSeconds));
  const [startError, setStartError] = useState<string | null>(null);
  const [endError, setEndError] = useState<string | null>(null);

  // Keep the inputs in sync when the underlying element changes (eg. via
  // selection switch or undo/redo) — but only when the input isn't focused,
  // so we don't fight the teacher mid-typing.
  useEffect(() => {
    const elementChanged = previousElementIdRef.current !== element.id;
    previousElementIdRef.current = element.id;
    if (!elementChanged && document.activeElement?.tagName === 'INPUT') return;
    setStartInput(formatVideoTimecode(content.startSeconds));
    setEndInput(formatVideoTimecode(content.endSeconds));
    setStartError(null);
    setEndError(null);
  }, [element.id, content.startSeconds, content.endSeconds]);

  const commitTimeInputs = useCallback(
    (nextStartInput = startInput, nextEndInput = endInput): boolean => {
      const result = parseTimeInputs(nextStartInput, nextEndInput);
      if (!result.ok) {
        setStartError(result.startError);
        setEndError(result.endError);
        return false;
      }

      setStartError(null);
      setEndError(null);
      if (
        result.patch.startSeconds !== content.startSeconds ||
        result.patch.endSeconds !== content.endSeconds
      ) {
        onUpdateContent(result.patch);
      }
      setStartInput(result.startInput);
      setEndInput(result.endInput);
      return true;
    },
    [content.endSeconds, content.startSeconds, endInput, onUpdateContent, startInput]
  );

  const closePopover = useCallback(() => {
    if (commitTimeInputs()) {
      setAnchorEl(null);
    }
  }, [commitTimeInputs]);

  // Outside-click + Escape close. The gear button is excluded so its onClick
  // toggle isn't undone by this same listener.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (paperRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      closePopover();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopover();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closePopover, open]);

  const toggleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (anchorEl) {
      closePopover();
      return;
    }
    setAnchorEl(e.currentTarget);
  };

  const applyCurrentTime = useCallback(
    (field: TimeField) => {
      void (async () => {
        const currentTime = await getCurrentTime(element.id);
        if (currentTime == null) return;
        const timeText = formatVideoTimecode(Math.round(currentTime));

        if (field === 'start') {
          setStartInput(timeText);
          commitTimeInputs(timeText, endInput);
          return;
        }

        setEndInput(timeText);
        commitTimeInputs(startInput, timeText);
      })();
    },
    [commitTimeInputs, element.id, endInput, getCurrentTime, startInput]
  );

  return (
    <>
      <Tooltip title="Video settings" disableInteractive>
        <IconButton
          ref={buttonRef}
          size="small"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={toggleOpen}
          aria-label="Video settings"
          style={style}
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            color: open ? 'primary.main' : 'text.secondary',
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <TuneOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchorEl}
        popperRef={(instance) => {
          popperInstanceRef.current = instance;
        }}
        placement="right-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        style={{ zIndex: 1300 }}
      >
        <Paper
          ref={paperRef}
          elevation={6}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ p: 1.5, width: 280, display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Tooltip title="Start time accepts seconds, m:ss, or h:mm:ss." disableInteractive>
                <TextField
                  size="small"
                  label="Start"
                  placeholder="0:00"
                  value={startInput}
                  onChange={(e) => setStartInput(e.target.value)}
                  onBlur={() => commitTimeInputs()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitTimeInputs();
                    }
                  }}
                  error={Boolean(startError)}
                  helperText={startError}
                  sx={{ flex: 1 }}
                  inputProps={{ 'aria-label': 'Video start time' }}
                />
              </Tooltip>
              <Tooltip
                title={
                  hasLivePreview
                    ? 'Start the video at this time.'
                    : 'Play the video to choose a start time.'
                }
                disableInteractive
              >
                <span>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!hasLivePreview}
                    onClick={() => applyCurrentTime('start')}
                    sx={{
                      alignSelf: 'flex-start',
                      minWidth: 0,
                      minHeight: 20,
                      px: 0.5,
                      py: 0.125,
                      borderRadius: 0.75,
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      fontSize: 11,
                      lineHeight: 1.2,
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'action.selected', color: 'text.primary' },
                      '&.Mui-disabled': { bgcolor: 'transparent' },
                    }}
                  >
                    Use current time
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Tooltip
                title="End time accepts seconds, m:ss, or h:mm:ss. Leave empty to play to the end."
                disableInteractive
              >
                <TextField
                  size="small"
                  label="End"
                  value={endInput}
                  onChange={(e) => setEndInput(e.target.value)}
                  onBlur={() => commitTimeInputs()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitTimeInputs();
                    }
                  }}
                  error={Boolean(endError)}
                  helperText={endError}
                  sx={{ flex: 1 }}
                  inputProps={{ 'aria-label': 'Video end time' }}
                />
              </Tooltip>
              <Tooltip
                title={
                  hasLivePreview
                    ? 'End the video at this time.'
                    : 'Play the video to choose an end time.'
                }
                disableInteractive
              >
                <span>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!hasLivePreview}
                    onClick={() => applyCurrentTime('end')}
                    sx={{
                      alignSelf: 'flex-start',
                      minWidth: 0,
                      minHeight: 20,
                      px: 0.5,
                      py: 0.125,
                      borderRadius: 0.75,
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      fontSize: 11,
                      lineHeight: 1.2,
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'action.selected', color: 'text.primary' },
                      '&.Mui-disabled': { bgcolor: 'transparent' },
                    }}
                  >
                    Use current time
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Switch
                size="small"
                checked={Boolean(content.autoplay)}
                onChange={(e) =>
                  onUpdateContent(
                    e.target.checked ? { autoplay: true, mute: true } : { autoplay: undefined }
                  )
                }
              />
            }
            label={<Typography variant="caption">Autoplay</Typography>}
          />

          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Switch
                size="small"
                checked={Boolean(content.mute)}
                onChange={(e) => onUpdateContent({ mute: e.target.checked || undefined })}
              />
            }
            label={<Typography variant="caption">Mute</Typography>}
          />

          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Switch
                size="small"
                checked={Boolean(content.forceCaptions)}
                onChange={(e) => onUpdateContent({ forceCaptions: e.target.checked || undefined })}
              />
            }
            label={<Typography variant="caption">Captions Default On</Typography>}
          />

          <Tooltip title="Description for screen readers." disableInteractive>
            <TextField
              size="small"
              label="Alt text"
              placeholder="Description"
              value={content.alt ?? ''}
              onChange={(e) => onUpdateContent({ alt: e.target.value || undefined })}
              fullWidth
              inputProps={{ 'aria-label': 'Video alt text' }}
            />
          </Tooltip>
        </Paper>
      </Popper>
    </>
  );
};

export default VideoSettingsControl;
